import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { AvatarFallback, AvatarImage, AvatarRoot } from '@/components/ui/misc';

/**
 * Ocho tonos estables para los avatares sin foto. Son literales para que
 * Tailwind los compile; el índice sale de un hash del nombre, así que la misma
 * persona siempre tiene el mismo color, aquí y en la app móvil.
 */
const AVATAR_TONES = [
  'bg-chart-1/15 text-chart-1',
  'bg-chart-2/15 text-chart-2',
  'bg-chart-3/15 text-chart-3',
  'bg-chart-4/15 text-chart-4',
  'bg-chart-5/15 text-chart-5',
  'bg-chart-6/15 text-chart-6',
  'bg-chart-7/15 text-chart-7',
  'bg-chart-8/15 text-chart-8',
] as const;

function toneFor(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(index);
    hash |= 0;
  }
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length];
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeClass: Record<AvatarSize, string> = {
  xs: 'size-6 text-2xs',
  sm: 'size-8 text-2xs',
  md: 'size-10 text-xs',
  lg: 'size-12 text-sm',
  xl: 'size-16 text-lg',
};

function BaseAvatar({
  name,
  imageUrl,
  size = 'sm',
  className,
  ring,
}: {
  name: string;
  imageUrl?: string | null;
  size?: AvatarSize;
  className?: string;
  ring?: boolean;
}) {
  return (
    <AvatarRoot className={cn(sizeClass[size], ring && 'ring-2 ring-surface', className)}>
      {imageUrl ? <AvatarImage src={imageUrl} alt="" /> : null}
      <AvatarFallback
        className={cn('font-semibold', toneFor(name))}
        // El nombre completo ya está en el texto contiguo; el avatar es
        // decorativo y repetirlo sólo alarga el recorrido del lector.
        aria-hidden="true"
      >
        {initials(name)}
      </AvatarFallback>
    </AvatarRoot>
  );
}

export function CustomerAvatar({
  customer,
  size,
  className,
}: {
  customer: { displayName: string };
  size?: AvatarSize;
  className?: string;
}) {
  return <BaseAvatar name={customer.displayName} size={size} className={className} />;
}

export function UserAvatar({
  user,
  size,
  className,
  ring,
}: {
  user: { fullName: string; avatarUrl?: string | null } | null | undefined;
  size?: AvatarSize;
  className?: string;
  ring?: boolean;
}) {
  if (!user) {
    return (
      <AvatarRoot className={cn(sizeClass[size ?? 'sm'], className)}>
        <AvatarFallback className="border border-dashed border-border-strong bg-transparent text-fg-subtle" aria-hidden="true">
          ?
        </AvatarFallback>
      </AvatarRoot>
    );
  }
  return <BaseAvatar name={user.fullName} imageUrl={user.avatarUrl} size={size} className={className} ring={ring} />;
}

/** Avatar con el nombre al lado: el patrón de celdas de tabla y cabeceras. */
export function UserChip({
  user,
  size = 'xs',
  emptyLabel = 'Sin asignar',
  className,
}: {
  user: { fullName: string; avatarUrl?: string | null; jobTitle?: string | null } | null | undefined;
  size?: AvatarSize;
  emptyLabel?: string;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <UserAvatar user={user} size={size} />
      <span className={cn('truncate text-sm', user ? 'text-fg' : 'italic text-fg-subtle')}>
        {user?.fullName ?? emptyLabel}
      </span>
    </span>
  );
}
